import { useTranslation } from 'react-i18next'

import { Box, Tooltip } from '@enechain/ecloud-designsystem'

import { SendErrorReasonTooltip } from '~/components/bidding/SendErrorReasonTooltip'
import { Badge } from '~/components/common/Badge'
import type { EnumBadgeSpec } from '~/components/common/EnumStatusBadge'
import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'
import {
  type IntradayStatusSource,
  type IntradayTableStatus,
  intradayStatusCellOf,
} from '~/pages/IntradayBidding/models/intradayTableStatus'

/**
 * 時間前入札のステータス表示 (入札一覧・入札履歴・板発注の自社入札表・詳細モーダル)。
 * 約定軸だけの `INTRADAY_STATUS_BADGE` (`~/components/bidding/BiddingIntradayStatusTag`) は
 * 3 軸を合成しないため、 AC-BIN-035 の区分名 (`page:biddingIntraday.tableStatus.*`) に
 * 合わせた専用 map を持つ。
 *
 * `composeIntradayTableStatus` (models/intradayTableStatus.ts) が約定軸 (bidStatus) +
 * 送信軸 (sendStatus) + 承認軸 (approvalState) から導出した合成ステータス
 * (`IntradayTableStatus`、 string union) をキーにする — `EnumStatusBadge` は proto の numeric enum 専用
 * (`E extends number`) のため使えず、 本 map は直接 `Badge` を描画する。
 *
 * 入札中・送信中は進行中のため `info`。
 * RESERVED (= 予約中) は進行中でも終端でもない待機のため `neutral`。
 * 下書きは未着手のため `neutral`、 承認待ちは人の対応待ちのため `warning`、 差戻しは
 * `negative`。
 *
 * unmatched (未約定) / expired (失効) は #7144 論点2 決定A (AC-BIN-019/035/036)
 * で追加された区分 (T-52)。 `INTRADAY_STATUS_BADGE` の同名値と同じ色判断を踏襲する
 * (未約定は失敗ではないため `neutral`、 失効は再送不能な終端のため `negative`)。
 *
 * TODO(#4917): spec 承認後に「予約中」の文言を確定する (現状は仮置き)。
 */
export const INTRADAY_TABLE_STATUS_BADGE: Record<
  IntradayTableStatus,
  EnumBadgeSpec
> = {
  draft: {
    status: 'neutral',
    labelKey: 'page:biddingIntraday.tableStatus.draft',
  },
  pendingApproval: {
    status: 'warning',
    labelKey: 'page:biddingIntraday.tableStatus.pendingApproval',
  },
  rejected: {
    status: 'negative',
    labelKey: 'page:biddingIntraday.tableStatus.rejected',
  },
  sending: {
    status: 'info',
    labelKey: 'page:biddingIntraday.tableStatus.sending',
  },
  waiting: {
    status: 'info',
    labelKey: 'page:biddingIntraday.tableStatus.waiting',
  },
  reserved: {
    status: 'neutral',
    labelKey: 'page:biddingIntraday.tableStatus.reserved',
  },
  partialMatched: {
    status: 'info',
    labelKey: 'page:biddingIntraday.tableStatus.partialMatched',
  },
  matched: {
    status: 'positive',
    labelKey: 'page:biddingIntraday.tableStatus.matched',
  },
  cancelled: {
    status: 'neutral',
    labelKey: 'page:biddingIntraday.tableStatus.cancelled',
  },
  autoCancelled: {
    status: 'neutral',
    labelKey: 'page:biddingIntraday.tableStatus.autoCancelled',
  },
  unmatched: {
    status: 'neutral',
    labelKey: 'page:biddingIntraday.tableStatus.unmatched',
  },
  expired: {
    status: 'negative',
    labelKey: 'page:biddingIntraday.tableStatus.expired',
  },
}

/**
 * GC を過ぎて JEPX が受け付けたかを確かめている札の補足 (AC-BIN-115、 design
 * `bidding/intraday.html` の `_STATUS_NOTES.checking`)。 ステータスは変えず、 バッジの横に添える。
 * ecloud の `Text` は `fontSize` を無視するので `Box` の span で xs にする。
 */
const ReceiptCheckingNote = (): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <Tooltip
      content={t('page:biddingIntraday.bidStatusNote.receiptCheckingHint')}
    >
      <Box
        as="span"
        marginLeft={1}
        color="Text.midEmphasis"
        fontSize="xs"
        whiteSpace="nowrap"
        cursor="help"
        tabIndex={0}
      >
        {t('page:biddingIntraday.bidStatusNote.receiptChecking')}
      </Box>
    </Tooltip>
  )
}

type Props = {
  /**
   * 1 札の状態。 入札一覧・入札履歴・板発注は `convertIntradayBidsFromOrders` が作る行を、
   * 詳細モーダルは詳細の札から組んだ値を渡す。 承認軸は backend の値のまま渡す
   * (`composeIntradayTableStatus`)。
   */
  bid: IntradayStatusSource &
    Pick<IntradayBidRow, 'sendErrorCategory' | 'sendErrorDetail'> &
    Partial<Pick<IntradayBidRow, 'receiptChecking'>>
}

/**
 * 時間前入札の 1 札のステータスセルの中身。 入札一覧と板発注の自社入札表が共有する
 * (表ごとに写像を持つと、 一方だけ区分名が古いまま・数値のまま残る)。
 *
 * 何を出すかの優先順位は `intradayStatusCellOf` が決める (AC-BIN-089a / AC-BIN-035 /
 * AC-BIN-059)。 ここはその結果を、 理由 tooltip 付きの業務ステータスバッジか業務ステータスの
 * バッジで描くだけにする。 JEPX の受け付けを確認するまでの札は、 送り直している札も
 * 「送信中」 で、 「送信失敗」 は出さない (#10680)。
 */
export const IntradayBidStatusCell = ({ bid }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { sendErrorCategory, sendErrorDetail } = bid
  const statusCell = intradayStatusCellOf(bid)
  const spec = INTRADAY_TABLE_STATUS_BADGE[statusCell.status]
  const badge = <Badge status={spec.status}>{t(spec.labelKey)}</Badge>
  const statusBadge = statusCell.withSendErrorReason ? (
    <SendErrorReasonTooltip
      errorCategory={sendErrorCategory}
      errorDetail={sendErrorDetail}
    >
      {badge}
    </SendErrorReasonTooltip>
  ) : (
    badge
  )
  // backend が確かめていると返した札だけに添える (送信中のままの札。 AC-BIN-115)。
  return statusCell.status === 'sending' && bid.receiptChecking === true ? (
    <>
      {statusBadge}
      <ReceiptCheckingNote />
    </>
  ) : (
    statusBadge
  )
}
